import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { MDBBadge, MDBBtn, MDBIcon, MDBTable } from "mdbreact";
import { axioKit, fullName } from "../../../../../services/utilities";
import {
  PURCHASE_URL,
  formatDate,
} from "../../../laboratory/purchaseOrders/common";
import { REASONS } from "./common";
import { unitText } from "./units";
import { packUnitOf } from "../../../laboratory/catalog/products/packagesEditor";

const EXPIRING_DAYS = 30;
const daysTo = (date) => (new Date(date) - new Date()) / 86400000;

const PACK_STATUS = {
  complete: { label: "Complete", color: "success" },
  open: { label: "Open", color: "warning" },
  used: { label: "Used up", color: "light" },
};

// pack status of the server (Inventory) -> label
const STATUS_OF = { sealed: "complete", open: "open", finished: "used" };

/**
 * The packs (Inventory) of every batch, as recorded: each unit taken is
 * linked to its pack, with who took it (patient or monthly expense).
 */
const packsOf = (batches, packs) =>
  batches.map((batch) => {
    const list = packs
      .filter(
        ({ purchaseItem, lotNo }) =>
          String(purchaseItem) === String(batch.lineId) &&
          (lotNo || "") === (batch.lot || ""),
      )
      .sort((a, b) => (a.packNo || 0) - (b.packNo || 0))
      .map((pack, index) => {
        const used = (pack.used || 0) + (pack.consume || 0);
        return {
          index,
          capacity: pack.qnty,
          used,
          left: pack.qnty - used,
          status: STATUS_OF[pack.status] || "complete",
          isFree: pack.isFree,
          opened: pack.opened,
          uses: pack.uses || [],
        };
      });
    return {
      ...batch,
      size: Math.max(Number(batch.unitSize) || 1, 1),
      packs: list,
    };
  });

export default function BatchDetails({ row, onChanged }) {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    [packs, setPacks] = useState([]),
    [openPacks, setOpenPacks] = useState({}),
    [isLoading, setIsLoading] = useState(false),
    { addToast } = useToasts();

  const branch = activePlatform?.branchId;

  const load = useCallback(() => {
    if (!branch || !token) return;
    setIsLoading(true);
    axioKit
      .universal(`${PURCHASE_URL}/usage`, token, {
        branch,
        product: row.product._id,
        ...(row.variant?._id && { variant: row.variant._id }),
      })
      .then(({ payload }) => setPacks(payload || []))
      .catch((error) =>
        addToast(error.message, { appearance: "error", autoDismiss: true }),
      )
      .finally(() => setIsLoading(false));
  }, [branch, token, row.product._id, row.variant, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const batches = useMemo(() => packsOf(row.batches, packs), [row, packs]);

  const handleFix = async (lineId) => {
    try {
      const { success } = await axioKit.update(
        PURCHASE_URL,
        { _id: lineId },
        token,
        "unitSize",
      );
      addToast(success, { appearance: "success", autoDismiss: true });
      onChanged();
    } catch (error) {
      addToast(error.message, { appearance: "error", autoDismiss: true });
    }
  };

  // isOpen: what is shown now (open packs start expanded)
  const togglePack = (id, isOpen) =>
    setOpenPacks((prev) => ({ ...prev, [id]: !isOpen }));

  const packCount = batches.reduce((sum, b) => sum + b.packs.length, 0);

  return (
    <>
      <h6 className="mb-2">
        <MDBIcon icon="boxes" className="mr-2" />
        Batch / Lot Details ({batches.length} batch
        {batches.length === 1 ? "" : "es"} · {packCount} {row.unit}
        {packCount === 1 ? "" : "s"})
        {isLoading && <MDBIcon icon="spinner" spin className="ml-2" />}
      </h6>

      {!batches.length && (
        <p className="text-muted small mb-0">No batch in the branch.</p>
      )}

      {batches.map((batch, batchIndex) => {
        const days = batch.expiry ? daysTo(batch.expiry) : null;
        // the pack size registered (breakdown); the received one may be wrong
        const registered = row.wrongPack || row.packQty;
        // the pack registered (e.g. pack), the row may show pcs while it is wrong
        const packUnit = packUnitOf(row.variant, row.product) || row.unit;
        // received with fewer units per pack than registered (e.g. 1 pc per pack
        // of 100): the line or its packs, ordered before the breakdown was set
        const smallPacks = batch.packs.filter(
          ({ capacity }) => (capacity || 0) < registered,
        ).length;
        const wrongSize =
          registered > 1 && (batch.size < registered || smallPacks > 0);
        return (
          <div key={batchIndex} className="border rounded bg-white mb-2">
            <div
              className="px-3 py-2 d-flex flex-wrap align-items-center grey lighten-4"
              style={{ gap: 16 }}
            >
              <span>
                <small className="text-muted">Lot</small>{" "}
                <b>{batch.lot || "—"}</b>
              </span>
              <span
                className={
                  days === null
                    ? ""
                    : days < 0
                      ? "text-danger font-weight-bold"
                      : days <= EXPIRING_DAYS
                        ? "text-warning"
                        : ""
                }
              >
                <small className="text-muted">Expiry</small>{" "}
                {batch.expiry ? formatDate(batch.expiry) : "—"}
                {days !== null && days < 0 && " (expired)"}
              </span>
              <span>
                <small className="text-muted">Received</small>{" "}
                {formatDate(batch.receivedAt) || "—"}
              </span>
              <span>
                <small className="text-muted">P.O.</small> {batch.po}
              </span>
              <span className="ml-auto small">
                {unitText(batch.packs.length, row.unit)} ·{" "}
                {batch.size > 1 && `${batch.size} ${row.base} each · `}
                <b>{batch.available}</b> {row.base} left
              </span>
            </div>

            {wrongSize && (
              <div className="alert alert-warning small py-1 px-3 mb-0 rounded-0 d-flex flex-wrap align-items-center">
                <span className="mr-2">
                  <MDBIcon icon="exclamation-triangle" className="mr-1" />
                  Received as 1 {row.base} per {packUnit} (
                  {smallPacks || batch.packs.length} {packUnit}(s)): the
                  packaging of this variant was not set yet when it was
                  requested. It should be 1 {packUnit} = {registered} {row.base}
                  . What was already used stays used.
                </span>
                <MDBBtn
                  size="sm"
                  color="warning"
                  className="m-0 ml-auto px-2 py-1"
                  onClick={() => handleFix(batch.lineId)}
                >
                  <MDBIcon icon="wrench" className="mr-1" /> Fix pack size
                </MDBBtn>
              </div>
            )}

            {/* every pack (box 1, box 2...) opens to who used it */}
            {batch.packs.map((pack) => {
              const id = `${batchIndex}-${pack.index}`;
              // open packs start expanded, the others closed
              const isOpen = openPacks[id] ?? pack.status === "open";
              const status = PACK_STATUS[pack.status];
              const patients = pack.uses.filter(
                ({ type }) => type === "patient",
              ).length;
              const expenses = pack.uses.length - patients;
              const label =
                row.unit === row.base
                  ? `#${pack.index + 1}`
                  : `${row.unit.charAt(0).toUpperCase()}${row.unit.slice(1)} ${
                      pack.index + 1
                    }`;
              return (
                <div key={id} className="border-top">
                  <div
                    role="button"
                    tabIndex={0}
                    className={`px-3 py-2 d-flex flex-wrap align-items-center ${
                      isOpen ? "blue lighten-5" : ""
                    } ${pack.status === "used" ? "text-muted" : ""}`}
                    style={{ cursor: "pointer", gap: 12 }}
                    onClick={() => togglePack(id, isOpen)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && togglePack(id, isOpen)
                    }
                  >
                    <MDBIcon
                      icon={isOpen ? "chevron-down" : "chevron-right"}
                      className="text-muted"
                      style={{ width: 12 }}
                    />
                    <b style={{ minWidth: 70 }}>
                      {label}{" "}
                      <small className="text-muted font-weight-normal">
                        of {batch.packs.length}
                      </small>
                    </b>
                    <MDBBadge color={status.color}>{status.label}</MDBBadge>
                    <span>
                      <b>{pack.left}</b>{" "}
                      <small className="text-muted">
                        available of {pack.capacity} {row.base}
                      </small>
                    </span>
                    <div
                      className="progress"
                      style={{ height: 5, width: 120 }}
                      title={`${pack.left} of ${pack.capacity} left`}
                    >
                      <div
                        className={`progress-bar bg-${
                          pack.status === "open" ? "warning" : "success"
                        }`}
                        role="progressbar"
                        style={{
                          width: `${(pack.left / pack.capacity) * 100}%`,
                        }}
                      />
                    </div>
                    <small className="ml-auto text-muted">
                      {pack.used > 0
                        ? `${pack.used} used${
                            patients ? ` · ${patients} patient(s)` : ""
                          }${expenses ? ` · ${expenses} expense(s)` : ""}`
                        : "not yet opened"}
                    </small>
                  </div>

                  {isOpen && (
                    <div className="px-4 pb-2 grey lighten-5">
                      {!pack.uses.length ? (
                        <small className="d-block text-muted py-2 pl-3">
                          {pack.used > 0
                            ? `${pack.used} ${row.base} used, no patient record found.`
                            : "Intact, not yet opened."}
                        </small>
                      ) : (
                        <MDBTable small className="mb-0">
                          <tbody>
                            {pack.uses.map((use, index) => {
                              const reason =
                                REASONS[use.reason] || REASONS.other;
                              return (
                                <tr key={index} className="small">
                                  <td style={{ width: 40 }}>
                                    {String.fromCharCode(97 + (index % 26))}.
                                  </td>
                                  <td>
                                    {use.type === "patient" ? (
                                      <>
                                        <MDBIcon
                                          icon="user"
                                          className="mr-1 text-muted"
                                        />
                                        {use.customer
                                          ? fullName(use.customer)
                                          : "Patient"}
                                      </>
                                    ) : (
                                      <>
                                        <MDBBadge
                                          color={reason.color}
                                          className="mr-1"
                                        >
                                          {reason.label}
                                        </MDBBadge>
                                        {use.remarks}
                                        {use.user &&
                                          ` · by ${fullName(use.user)}`}
                                      </>
                                    )}
                                  </td>
                                  <td className="text-right">
                                    {use.qty}{" "}
                                    {use.qty === 1 && row.base === "pcs"
                                      ? "pc"
                                      : row.base}
                                  </td>
                                  <td
                                    className="text-right text-muted text-nowrap"
                                    style={{ width: 110 }}
                                  >
                                    {formatDate(use.date)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </MDBTable>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
